import type { DropResult } from 'react-beautiful-dnd'
import { useDispatch } from 'react-redux'
import * as R from 'ramda'
import type { Nil } from '@pbt/pbt-ui-components'

import type { RetailOrderLineItem } from '~/api/graphql/generated/types'
import { rearrangeGroupedLineItems } from '~/store/actions/finance'
import type { InvoiceLineItem } from '~/types'

import { calculateDragRearrangements } from '../utils/tableHelpers'

/**
 * Hook for handling drag-and-drop rearrangement of charge sheet items
 * Supports both Chewy checkout mode (rearrangeGroupedLineItems) and legacy mode
 * Updates local state for immediate UI feedback
 *
 * @param chargesId - Charge sheet ID for rearrangement
 * @param items - Current array of items
 * @param setLocalItems - State setter for local items (for immediate UI update)
 * @param isChewyCheckoutEnabled - Whether Chewy checkout is enabled
 * @returns Handler function for drag end events
 */
export const useChargeItemDragDrop = (
  chargesId: string | Nil,
  items: (InvoiceLineItem | RetailOrderLineItem)[],
  setLocalItems: (items: (InvoiceLineItem | RetailOrderLineItem)[]) => void,
  isChewyCheckoutEnabled: boolean,
) => {
  const dispatch = useDispatch()

  return (result: DropResult) => {
    if (!result.destination || !chargesId) return

    const from = result.source.index
    const to = result.destination.index

    if (from === to) return

    const newItemsOrder = R.move(from, to, items)

    if (isChewyCheckoutEnabled) {
      const rearrangements = calculateDragRearrangements(items, from, to)
      dispatch(rearrangeGroupedLineItems(chargesId, rearrangements))
    }

    setLocalItems(newItemsOrder)
  }
}
